import React, { useRef, useEffect, useState, Suspense } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { Link } from "react-router-dom";

/* ═══════════════════════════════════════════════════════════
   ERROR BOUNDARY — Graceful fallback if WebGL fails
   ═══════════════════════════════════════════════════════════ */
class CanvasErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/* ═══════════════════════════════════════════════════════════
   IMPERATIVE THREE.JS SCENE
   All objects created via scene.add() to avoid babel/HMR
   plugins injecting x-line-number attributes into R3F's
   declarative JSX elements.
   ═══════════════════════════════════════════════════════════ */

const STEEL_HEX = 0x1e4e8c;
const STEEL_LIGHT_HEX = 0x5790c9;
const EMBER_HEX = 0xf26b1f;

function SceneContent() {
  const { scene } = useThree();
  const groupRef = useRef(null);
  const orbRef = useRef(null);
  const dustRef = useRef(null);
  const ringsRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });

  /* ─── Mouse tracking for magnetic tilt ─── */
  useEffect(() => {
    const handlePointer = (e) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      mouseRef.current.tx = (e.clientX / w) * 2 - 1;
      mouseRef.current.ty = -((e.clientY / h) * 2 - 1);
    };
    window.addEventListener("pointermove", handlePointer, { passive: true });
    return () => window.removeEventListener("pointermove", handlePointer);
  }, []);

  useEffect(() => {
    /* ─── Main Group (positioned right-of-center) ─── */
    const group = new THREE.Group();
    group.position.set(1.8, 0.15, 0);

    /* ─── Particle Orb: 2200 particles ─── */
    const ORB_COUNT = 2200;
    const orbGeo = new THREE.BufferGeometry();
    const orbPos = new Float32Array(ORB_COUNT * 3);
    const orbCol = new Float32Array(ORB_COUNT * 3);
    const c = new THREE.Color();
    const steelC = new THREE.Color(STEEL_HEX);
    const steelLightC = new THREE.Color(STEEL_LIGHT_HEX);

    for (let i = 0; i < ORB_COUNT; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 1.5 + (Math.random() - 0.5) * 0.55;
      orbPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      orbPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      orbPos[i * 3 + 2] = r * Math.cos(phi);

      if (Math.random() < 0.06) {
        c.setHex(EMBER_HEX);
      } else {
        c.lerpColors(steelC, steelLightC, Math.random());
      }
      orbCol[i * 3] = c.r;
      orbCol[i * 3 + 1] = c.g;
      orbCol[i * 3 + 2] = c.b;
    }
    orbGeo.setAttribute("position", new THREE.BufferAttribute(orbPos, 3));
    orbGeo.setAttribute("color", new THREE.BufferAttribute(orbCol, 3));

    const orbMat = new THREE.PointsMaterial({
      size: 0.065,
      vertexColors: true,
      transparent: true,
      opacity: 0.92,
      sizeAttenuation: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const orbPoints = new THREE.Points(orbGeo, orbMat);
    group.add(orbPoints);
    orbRef.current = orbPoints;

    /* ─── 3 Orbital Data Rings ─── */
    const ringConfigs = [
      { radius: 2.2, tiltX: 0.4, tiltZ: 0.15, speed: 0.35, count: 55, color: 0x2e70ad },
      { radius: 2.7, tiltX: -0.55, tiltZ: 0.3, speed: -0.25, count: 45, color: STEEL_HEX },
      { radius: 3.2, tiltX: 0.75, tiltZ: -0.2, speed: 0.18, count: 35, color: EMBER_HEX },
    ];

    const rings = ringConfigs.map((cfg) => {
      const rGeo = new THREE.BufferGeometry();
      rGeo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(cfg.count * 3), 3)
      );
      const rMat = new THREE.PointsMaterial({
        size: 0.035,
        color: cfg.color,
        transparent: true,
        opacity: 0.6,
        sizeAttenuation: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const rPts = new THREE.Points(rGeo, rMat);
      rPts.rotation.set(cfg.tiltX, 0, cfg.tiltZ);
      group.add(rPts);
      return {
        points: rPts,
        offsets: Array.from({ length: cfg.count }, () => Math.random() * Math.PI * 2),
        ...cfg,
      };
    });
    ringsRef.current = rings;

    scene.add(group);
    groupRef.current = group;

    /* ─── Space Dust: 250 ambient particles ─── */
    const DUST_COUNT = 250;
    const dustGeo = new THREE.BufferGeometry();
    const dustPos = new Float32Array(DUST_COUNT * 3);
    for (let i = 0; i < DUST_COUNT; i++) {
      dustPos[i * 3] = (Math.random() - 0.5) * 22;
      dustPos[i * 3 + 1] = (Math.random() - 0.5) * 14;
      dustPos[i * 3 + 2] = (Math.random() - 0.5) * 14;
    }
    dustGeo.setAttribute("position", new THREE.BufferAttribute(dustPos, 3));
    const dustMat = new THREE.PointsMaterial({
      size: 0.018,
      color: STEEL_LIGHT_HEX,
      transparent: true,
      opacity: 0.4,
      sizeAttenuation: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const dustPts = new THREE.Points(dustGeo, dustMat);
    scene.add(dustPts);
    dustRef.current = dustPts;

    /* ─── Cleanup ─── */
    return () => {
      scene.remove(group);
      scene.remove(dustPts);
      orbGeo.dispose();
      orbMat.dispose();
      rings.forEach((r) => {
        r.points.geometry.dispose();
        r.points.material.dispose();
      });
      dustGeo.dispose();
      dustMat.dispose();
    };
  }, [scene]);

  /* ─── Animation Loop ─── */
  useFrame(({ clock }, delta) => {
    const t = clock.elapsedTime;

    /* Magnetic mouse follow — lerp toward target for smooth tracking */
    const m = mouseRef.current;
    m.x += (m.tx - m.x) * 0.06;
    m.y += (m.ty - m.y) * 0.06;

    /* Group magnetic tilt + parallax lean toward cursor */
    if (groupRef.current) {
      groupRef.current.rotation.y = m.x * 0.45;
      groupRef.current.rotation.x = -m.y * 0.32;
      groupRef.current.position.x = 1.8 + m.x * 0.22;
      groupRef.current.position.y = 0.15 + m.y * 0.12;
    }

    /* Orb rotation */
    if (orbRef.current) {
      orbRef.current.rotation.y = t * 0.05;
      orbRef.current.rotation.x = Math.sin(t * 0.12) * 0.06;
    }

    /* Orbital ring particle flow */
    ringsRef.current.forEach((ring) => {
      const arr = ring.points.geometry.attributes.position.array;
      for (let i = 0; i < ring.count; i++) {
        ring.offsets[i] += ring.speed * delta;
        const a = ring.offsets[i];
        arr[i * 3] = ring.radius * Math.cos(a);
        arr[i * 3 + 1] = Math.sin(a * 3) * 0.04;
        arr[i * 3 + 2] = ring.radius * Math.sin(a);
      }
      ring.points.geometry.attributes.position.needsUpdate = true;
    });

    /* Space dust drift */
    if (dustRef.current) {
      dustRef.current.rotation.y = t * 0.008;
    }
  });

  return null;
}

/* ═══════════════════════════════════════════════════════════
   ROTATING HEADLINES — Typewriter with ember/italic keywords
   ═══════════════════════════════════════════════════════════ */
const HEADLINES = [
  [
    { text: "The world is building ", s: "" },
    { text: "$106 trillion", s: "ember" },
    { text: " of infrastructure ", s: "" },
    { text: "on tools from the past", s: "ice" },
    { text: ".", s: "" },
  ],
  [
    { text: "The ", s: "" },
    { text: "infrastructure supercycle", s: "ember" },
    { text: " needs a ", s: "" },
    { text: "cognitive backbone", s: "ice" },
    { text: ".", s: "" },
  ],
  [
    { text: "Eight ", s: "" },
    { text: "AI Platforms", s: "ember" },
    { text: ". One ", s: "" },
    { text: "Cognition Engine", s: "ice" },
    { text: ". Zero ", s: "" },
    { text: "Blind Spots", s: "ember" },
    { text: ".", s: "" },
  ],
  [
    { text: "From ", s: "" },
    { text: "Concept", s: "ice" },
    { text: " to ", s: "" },
    { text: "Commissioning", s: "ice" },
    { text: " \u2014 AI That Speaks Your ", s: "" },
    { text: "Industry\u2019s Language", s: "ember" },
  ],
];

/* ═══════════════════════════════════════════════════════════
   MAIN HERO — Z-0 to Z-7 layered architecture
   ═══════════════════════════════════════════════════════════ */
export function Hero() {
  const [headlineIdx, setHeadlineIdx] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [done, setDone] = useState(false);

  const segments = HEADLINES[headlineIdx];
  const totalLen = segments.reduce((sum, seg) => sum + seg.text.length, 0);

  /* Character-by-character typing */
  useEffect(() => {
    if (done) return;
    if (charCount >= totalLen) { setDone(true); return; }
    const t = setTimeout(() => setCharCount((c) => c + 1), 38);
    return () => clearTimeout(t);
  }, [charCount, totalLen, done]);

  /* 5-second pause after sentence completes, then advance */
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => {
      setHeadlineIdx((i) => (i + 1) % HEADLINES.length);
      setCharCount(0);
      setDone(false);
    }, 5000);
    return () => clearTimeout(t);
  }, [done]);

  /* Render typed text with ember/italic styling */
  const renderTyped = () => {
    let offset = 0;
    return segments.map((seg, i) => {
      const start = offset;
      offset += seg.text.length;
      const visible = Math.max(0, Math.min(seg.text.length, charCount - start));
      if (visible === 0) return null;
      const cls =
        seg.s === "ember" ? "hero-imm-ember" : seg.s === "ice" ? "hero-imm-ice" : undefined;
      return (
        <span key={i} className={cls}>
          {seg.text.substring(0, visible)}
        </span>
      );
    });
  };
  return (
    <section className="hero-imm" id="top" data-testid="hero-section">
      {/* Z-0: Deep space gradient background */}
      <div className="hero-imm-bg" aria-hidden="true" />

      {/* Z-1: Subtle coordinate grid */}
      <div className="hero-imm-grid" aria-hidden="true" />

      {/* Z-2 / Z-3: React Three Fiber canvas */}
      <div className="hero-imm-canvas" aria-hidden="true">
        <CanvasErrorBoundary>
          <Canvas
            camera={{ position: [0, 0.3, 6], fov: 45 }}
            dpr={[1, 1.5]}
            gl={{
              antialias: false,
              alpha: true,
              powerPreference: "high-performance",
            }}
            style={{ pointerEvents: "none" }}
          >
            <Suspense fallback={null}>
              <SceneContent />
            </Suspense>
          </Canvas>
        </CanvasErrorBoundary>
      </div>

      {/* Z-4: Ambient glow + scrim for readability */}
      <div className="hero-imm-glow" aria-hidden="true" />

      {/* Z-5: Floating constellation — balances right-side particle density */}
      <div className="hero-imm-constellation" aria-hidden="true" data-testid="hero-constellation">
        <svg viewBox="0 0 220 220" preserveAspectRatio="xMidYMid meet">
          <line x1="30" y1="50" x2="100" y2="110" className="hc-line hc-line-1" />
          <line x1="100" y1="110" x2="160" y2="70" className="hc-line hc-line-2" />
          <line x1="100" y1="110" x2="185" y2="165" className="hc-line hc-line-3" />
          <circle cx="30" cy="50" r="2.8" className="hc-dot hc-dot-1" />
          <circle cx="100" cy="110" r="3.6" className="hc-dot hc-dot-2" />
          <circle cx="160" cy="70" r="2.2" className="hc-dot hc-dot-3" />
          <circle cx="185" cy="165" r="2.6" className="hc-dot hc-dot-4" />
        </svg>
      </div>

      {/* Z-6: Main content — left-aligned copy */}
      <div className="hero-imm-content" data-testid="hero-copy">
        <div
          className="hero-imm-eyebrow hero-imm-rise"
          style={{ animationDelay: "200ms" }}
          data-testid="hero-eyebrow"
        >
          <span className="hero-imm-dot" />
          From diagnosis to delivery — AI platforms for Infra & Capital
          Projects
        </div>

        <h1 className="hero-imm-h1" data-testid="hero-h1">
          <div className="hero-h1-typewriter hero-imm-rise" style={{ animationDelay: "400ms" }}>
            {renderTyped()}
            <span className={`hero-cursor${done ? " blink" : ""}`} />
          </div>
        </h1>

        <div
          className="hero-imm-strat hero-imm-rise"
          style={{ animationDelay: "1000ms" }}
          data-testid="hero-strategy"
        >
          <div className="hero-imm-strat-label">Our Strategic Stack</div>
          <div className="hero-imm-strat-pills">
            <span className="hero-imm-pill">
              <span
                className="hero-imm-pill-dot"
                style={{ background: "#1E4E8C" }}
              />
              Diagnose &amp; Assess
            </span>
            <span className="hero-imm-pill">
              <span
                className="hero-imm-pill-dot"
                style={{ background: "#2E70AD" }}
              />
              Engineer &amp; Control
            </span>
            <span className="hero-imm-pill">
              <span
                className="hero-imm-pill-dot"
                style={{ background: "#F26B1F" }}
              />
              Procure &amp; Contract
            </span>
          </div>
        </div>

        <p
          className="hero-imm-sub hero-imm-rise"
          style={{ animationDelay: "1140ms" }}
          data-testid="hero-sub"
        >
          Refineries, power plants, highways, metros, data centers, LNG
          terminals &mdash; <strong>$106 trillion</strong> of capital projects
          are in execution or planning through 2040. The industry managing this
          buildout still runs on Excel, email, and software designed before the
          smartphone existed. Capexara AI offers a cognitive suite of platforms
          that changes this.
        </p>

        <div
          className="hero-imm-ctas hero-imm-rise"
          style={{ animationDelay: "1280ms" }}
        >
          <Link
            to="/contact"
            className="hero-imm-btn-primary"
            data-testid="hero-cta-request-demo"
          >
            Request demo <span className="hero-imm-arrow">&rarr;</span>
          </Link>
          <Link
            to="/platforms"
            className="hero-imm-btn-ghost"
            data-testid="hero-cta-see-platforms"
          >
            <span className="hero-imm-play">
              <span />
            </span>
            See the platforms
          </Link>
        </div>

      </div>

      {/* Z-7: Scroll cue */}
      <div className="hero-imm-scroll" aria-hidden="true">
        <span>scroll</span>
        <svg width="12" height="20" viewBox="0 0 12 20">
          <path
            d="M6 2v14M2 12l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.4"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </section>
  );
}
